/**
 * Universal CSV Parser
 *
 * Single header-alias-driven CSV parser with source-specific value maps.
 * Replaces Dragon Shield, DelverLens (v1+v2), and Moxfield parsers.
 *
 * Key features:
 * - Header alias map: all known CSV headers → canonical field names
 * - Source fingerprints: auto-detect source from matched canonical fields
 * - Source-specific value maps: condition, finish, language transforms per source
 * - Full re-parse on source change: same CSV rows, different value maps
 */
import { parseCSV } from '@/lib/csv';
import { HEADER_ALIASES, IMPORT_FIELDS, SOURCE_FINGERPRINTS, type CanonicalField } from '@/lib/importConstants';
import type { Condition, Finish, ImportSource, Language } from '@/types/card';
import type { ImportCard } from '@/types/schemas';

// ============================================================================
// Types
// ============================================================================

/**
 * Matching strategy for duplicate detection
 * - 'source_id': Match by source_system + source_id (Delver Local ID present)
 * - 'fingerprint': Match by scryfall_id + finish + condition
 * - 'none': No reliable matching available
 */
export type MatchingStrategy = 'source_id' | 'fingerprint' | 'none';

/**
 * Tracks which fields were found, used, and ignored during parsing
 */
export interface ImportFieldSummary {
  /** Source name for display (e.g., "DelverLens", "Dragon Shield") */
  sourceName: string;
  /** Fields actively used for card data */
  fieldsUsed: string[];
  /** Fields ignored (not needed) */
  fieldsIgnored: string[];
  /** Optional fields that were missing from the CSV */
  fieldsMissing?: string[];
  /** Format version detected (for sources with multiple versions) */
  formatVersion?: string;
  /** Strategy for duplicate detection */
  matchingStrategy: MatchingStrategy;
}

export interface UniversalCSVParseResult {
  cards: ImportCard[];
  fieldSummary: ImportFieldSummary;
  detectedSource: ImportSource;
}

// ============================================================================
// Source-specific value transforms
// ============================================================================

interface SourceValueMaps {
  /** Display name for summary */
  displayName: string;
  /** Map raw condition string → Condition */
  conditionMap: Record<string, Condition>;
  /** Normalize raw condition before lookup */
  conditionNormalize: (raw: string) => string;
  /** Extract finish from canonical field values */
  finishFn: (fields: Record<string, string | undefined>) => Finish;
  /** Map raw language string → Language */
  languageMap: Record<string, Language>;
  /** Default currency when purchase price is present but no explicit currency */
  defaultCurrency: string | null;
  /** Expected canonical fields for this source (for field summary) */
  expectedFields: CanonicalField[];
}

// ============================================================================
// Shared Language Map
// ============================================================================

/** Comprehensive language map covering full names, short codes, and aliases */
const FULL_LANGUAGE_MAP: Record<string, Language> = {
  // Full names
  english: 'en',
  spanish: 'es',
  french: 'fr',
  german: 'de',
  italian: 'it',
  portuguese: 'pt',
  japanese: 'ja',
  korean: 'ko',
  russian: 'ru',
  'simplified chinese': 'zhs',
  'traditional chinese': 'zht',
  phyrexian: 'ph',
  // Short codes (ISO/Scryfall)
  en: 'en',
  es: 'es',
  fr: 'fr',
  de: 'de',
  it: 'it',
  pt: 'pt',
  ja: 'ja',
  ko: 'ko',
  ru: 'ru',
  zhs: 'zhs',
  zht: 'zht',
  ph: 'ph',
  // Alternative short codes
  sp: 'es',
  jp: 'ja',
  kr: 'ko',
  cs: 'zhs',
  ct: 'zht',
};

// ============================================================================
// Source-Specific Value Maps
// ============================================================================

/**
 * Dragon Shield normalization: strips whitespace, hyphens, and underscores
 * before condition lookup. This means "Near Mint", "NearMint", "near-mint"
 * all normalize to "nearmint".
 */
function dragonShieldConditionNormalize(raw: string): string {
  return raw.toLowerCase().replace(/[\s\-_]/g, '');
}

/** Standard normalization: lowercase + trim only */
function standardConditionNormalize(raw: string): string {
  return raw.toLowerCase().trim();
}

/** Extract finish from the "finish" (Printing/Foil) canonical field */
function finishFromPrintingField(fields: Record<string, string | undefined>): Finish {
  const raw = (fields['finish'] ?? '').toLowerCase().trim();
  if (raw === 'foil' || raw === 'true' || raw === '1') return 'foil';
  if (raw === 'etched') return 'etched';
  return 'nonfoil';
}

/**
 * Extract finish from DelverLens v2 Foil/Etched + Foil columns.
 * Prioritizes "Foil/Etched" column, falls back to "Foil" column.
 */
function finishFromFoilEtchedField(fields: Record<string, string | undefined>): Finish {
  const foilEtched = (fields['foilEtched'] ?? '').toLowerCase().trim();
  if (foilEtched === 'etched') return 'etched';
  if (foilEtched === 'foil') return 'foil';

  const foil = (fields['finish'] ?? '').toLowerCase().trim();
  if (foil === 'foil' || foil === 'true' || foil === '1') return 'foil';

  return 'nonfoil';
}

/** DelverLens v1: boolean "Foil" field (true/false/1/0) */
function finishFromBooleanFoil(fields: Record<string, string | undefined>): Finish {
  const raw = (fields['finish'] ?? '').toLowerCase().trim();
  if (raw === 'true' || raw === '1' || raw === 'foil') return 'foil';
  return 'nonfoil';
}

const SOURCE_VALUE_MAPS: Record<string, SourceValueMaps> = {
  dragonshield: {
    displayName: 'Dragon Shield',
    conditionMap: {
      mint: 'M',
      nearmint: 'NM',
      excellent: 'NM',
      good: 'LP',
      lightlyplayed: 'LP',
      lightplay: 'LP',
      lightplayed: 'MP',
      moderatelyplayed: 'MP',
      moderateplay: 'MP',
      played: 'HP',
      heavilyplayed: 'HP',
      heavyplay: 'HP',
      damaged: 'D',
      poor: 'D',
    },
    conditionNormalize: dragonShieldConditionNormalize,
    finishFn: finishFromPrintingField,
    languageMap: FULL_LANGUAGE_MAP,
    defaultCurrency: 'USD',
    expectedFields: [
      'name',
      'setCode',
      'collectorNumber',
      'quantity',
      'condition',
      'finish',
      'language',
      'purchasePrice',
      'purchaseDate',
      'folderName',
    ],
  },

  delverlens: {
    displayName: 'DelverLens',
    conditionMap: {
      // Full names
      mint: 'M',
      'near mint': 'NM',
      'lightly played': 'LP',
      'moderately played': 'MP',
      'heavily played': 'HP',
      damaged: 'D',
      // Short codes
      m: 'M',
      nm: 'NM',
      lp: 'LP',
      mp: 'MP',
      hp: 'HP',
      d: 'D',
      // Compacted alternatives
      nearmint: 'NM',
      lightlyplayed: 'LP',
      moderatelyplayed: 'MP',
      heavilyplayed: 'HP',
    },
    conditionNormalize: standardConditionNormalize,
    // Finish function is dynamically selected based on whether foilEtched is present (v2 vs v1)
    finishFn: finishFromFoilEtchedField,
    languageMap: FULL_LANGUAGE_MAP,
    defaultCurrency: null,
    expectedFields: [
      'name',
      'setCode',
      'collectorNumber',
      'quantity',
      'foilEtched',
      'finish',
      'condition',
      'language',
      'purchasePrice',
      'purchaseCurrency',
      'folderName',
      'sourceId',
    ],
  },

  moxfield: {
    displayName: 'Moxfield',
    conditionMap: {
      m: 'M',
      mint: 'M',
      nm: 'NM',
      nearmint: 'NM',
      'near mint': 'NM',
      lp: 'LP',
      lightlyplayed: 'LP',
      'lightly played': 'LP',
      mp: 'MP',
      played: 'MP',
      moderatelyplayed: 'MP',
      'moderately played': 'MP',
      hp: 'HP',
      heavilyplayed: 'HP',
      'heavily played': 'HP',
      d: 'D',
      dm: 'D',
      damaged: 'D',
    },
    conditionNormalize: standardConditionNormalize,
    finishFn: finishFromPrintingField,
    languageMap: FULL_LANGUAGE_MAP,
    defaultCurrency: 'USD',
    expectedFields: [
      'name',
      'setCode',
      'collectorNumber',
      'quantity',
      'condition',
      'language',
      'finish',
      'isAltered',
      'isPlaytest',
      'purchasePrice',
    ],
  },

  // Fallback for unknown sources — uses conservative defaults
  unknown: {
    displayName: 'Unknown',
    conditionMap: {
      m: 'M',
      mint: 'M',
      nm: 'NM',
      nearmint: 'NM',
      'near mint': 'NM',
      lp: 'LP',
      lightlyplayed: 'LP',
      'lightly played': 'LP',
      mp: 'MP',
      moderatelyplayed: 'MP',
      'moderately played': 'MP',
      hp: 'HP',
      heavilyplayed: 'HP',
      'heavily played': 'HP',
      d: 'D',
      damaged: 'D',
    },
    conditionNormalize: standardConditionNormalize,
    finishFn: finishFromPrintingField,
    languageMap: FULL_LANGUAGE_MAP,
    defaultCurrency: null,
    expectedFields: ['name', 'quantity'],
  },
};

// ============================================================================
// UUID Validation
// ============================================================================

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUUID(id: string | undefined): boolean {
  return !!id && UUID_REGEX.test(id);
}

// ============================================================================
// Helper: parse boolean from CSV string
// ============================================================================

function parseBooleanField(raw: string | undefined): boolean {
  if (!raw) return false;
  const normalized = raw.trim().toUpperCase();
  return normalized === 'TRUE' || normalized === '1';
}

// ============================================================================
// Source Detection from Canonical Fields
// ============================================================================

/**
 * Auto-detect import source from the set of canonical fields present in the CSV.
 * Tries each fingerprint in priority order (most specific first).
 */
function detectSourceFromFields(canonicalFields: Set<CanonicalField>, text: string): ImportSource {
  for (const fp of SOURCE_FINGERPRINTS) {
    const hasRequired = fp.requiredFields.every((f) => canonicalFields.has(f));
    if (!hasRequired) continue;

    const hasExcluded = fp.excludedFields?.some((f) => canonicalFields.has(f));
    if (hasExcluded) continue;

    if (fp.extraCheck && !fp.extraCheck(text)) continue;

    return fp.source;
  }

  return 'unknown';
}

// ============================================================================
// Main Parser
// ============================================================================

/**
 * Parse any recognized CSV format into ImportCard[].
 *
 * @param text - Raw CSV text
 * @param source - Force a specific source (skips auto-detection).
 *   When the user changes the source dropdown, pass the new source here
 *   to re-parse with different value maps (condition/finish/language remap).
 * @returns Parsed cards, field summary, and detected source
 */
export function parseUniversalCSV(text: string, source?: ImportSource): UniversalCSVParseResult {
  // 1. Parse CSV rows
  const rows = parseCSV<Record<string, string>>(text);

  if (rows.length === 0) {
    return {
      cards: [],
      fieldSummary: {
        sourceName: 'Unknown',
        fieldsUsed: [],
        fieldsIgnored: [],
        matchingStrategy: 'none',
      },
      detectedSource: source ?? 'unknown',
    };
  }

  // 2. Map headers to canonical fields
  const rawHeaders = Object.keys(rows[0]);
  const headerToCanonical = new Map<string, CanonicalField>();
  const canonicalFields = new Set<CanonicalField>();

  for (const header of rawHeaders) {
    const canonical = HEADER_ALIASES[header.toLowerCase().trim()];
    if (canonical) {
      headerToCanonical.set(header, canonical);
      canonicalFields.add(canonical);
    }
  }

  // 3. Auto-detect source from canonical fields (if not provided)
  const detectedSource = source ?? detectSourceFromFields(canonicalFields, text);

  // Determine effective source for value maps
  // 'delverlens-legacy' uses the same value maps as 'delverlens'
  const effectiveSource = detectedSource === 'delverlens-legacy' ? 'delverlens' : detectedSource === 'manual' ? 'unknown' : detectedSource;
  const valueMaps = SOURCE_VALUE_MAPS[effectiveSource] ?? SOURCE_VALUE_MAPS['unknown'];

  // Determine if this is a v1 vs v2 DelverLens format
  const isDelverV2 = canonicalFields.has('sourceId');
  const isDelverV1 = effectiveSource === 'delverlens' && !isDelverV2;

  // Select the right finish function for DelverLens v1 (boolean Foil) vs v2 (Foil/Etched)
  const finishFn = isDelverV1 ? finishFromBooleanFoil : valueMaps.finishFn;

  // 4. Parse each row
  const cards: ImportCard[] = [];
  const foundRawHeaders = new Set<string>();
  const foundCanonicalFields = new Set<CanonicalField>();

  for (const row of rows) {
    // Track which raw headers have non-empty values
    for (const header of rawHeaders) {
      const val = row[header];
      if (val !== undefined && val !== '') {
        foundRawHeaders.add(header);
        const canonical = headerToCanonical.get(header);
        if (canonical) foundCanonicalFields.add(canonical);
      }
    }

    // Build a canonical field → value lookup for this row
    const fieldValues: Record<string, string | undefined> = {};
    for (const header of rawHeaders) {
      const canonical = headerToCanonical.get(header);
      if (canonical) {
        // For fields with multiple headers mapping to the same canonical field,
        // prefer the one with a non-empty value, and don't overwrite an existing value
        if (!fieldValues[canonical] || !fieldValues[canonical]!.trim()) {
          fieldValues[canonical] = row[header];
        }
      }
    }

    // Extract name (required)
    const name = fieldValues['name']?.trim();
    if (!name) continue;

    // Extract quantity
    const qty = parseInt(fieldValues['quantity'] ?? '1', 10);
    if (isNaN(qty) || qty <= 0) continue;

    // Map condition through source-specific value map
    const rawCondition = fieldValues['condition'] ?? '';
    const normalizedCondition = valueMaps.conditionNormalize(rawCondition);
    const condition: Condition = valueMaps.conditionMap[normalizedCondition] ?? 'NM';

    // Map finish through source-specific function
    const finish: Finish = finishFn(fieldValues);

    // Map language through shared language map
    const rawLanguage = (fieldValues['language'] ?? '').toLowerCase().trim();
    const language: Language = valueMaps.languageMap[rawLanguage] ?? 'en';

    // Set code
    const setCode = fieldValues['setCode']?.toLowerCase().trim() || null;

    // Collector number
    const collectorNumber = fieldValues['collectorNumber']?.trim() || null;

    // Purchase price
    const priceRaw = fieldValues['purchasePrice'];
    const purchasePrice = priceRaw ? parseFloat(priceRaw) : null;
    const purchaseCurrency =
      fieldValues['purchaseCurrency']?.trim() ||
      (purchasePrice != null && !isNaN(purchasePrice) ? (valueMaps.defaultCurrency ?? null) : null);

    // Purchase date
    const purchaseDate = fieldValues['purchaseDate']?.trim() || null;

    // Folder name
    const folderName = fieldValues['folderName']?.trim() || null;

    // Boolean flags
    const isAltered = parseBooleanField(fieldValues['isAltered']);
    const isPlaytest = parseBooleanField(fieldValues['isPlaytest']);

    // Source ID (DelverLens v2)
    const sourceId = fieldValues['sourceId']?.trim() || null;

    // Scryfall ID (validated as UUID)
    const rawScryfallId = fieldValues['scryfallId']?.trim();
    const scryfallId = isValidUUID(rawScryfallId) ? rawScryfallId! : null;

    cards.push({
      name,
      setCode,
      collectorNumber,
      quantity: qty,
      finish,
      condition,
      language,
      purchasePrice: purchasePrice != null && !isNaN(purchasePrice) ? purchasePrice : null,
      purchaseCurrency: purchasePrice != null && !isNaN(purchasePrice) ? purchaseCurrency : null,
      purchaseDate,
      isAltered,
      isPlaytest,
      folderName,
      sourceId,
      scryfallId,
    });
  }

  // 5. Build field summary
  const fieldsUsed: string[] = [];
  const fieldsIgnored: string[] = [];

  for (const header of rawHeaders) {
    if (!foundRawHeaders.has(header)) continue;
    const canonical = headerToCanonical.get(header);
    if (!canonical) continue;
    if (IMPORT_FIELDS.has(canonical)) {
      fieldsUsed.push(header);
    } else {
      fieldsIgnored.push(header);
    }
  }

  // Calculate matching strategy
  const hasSourceIds = cards.some((c) => c.sourceId);
  const hasSetAndCollector = cards.some((c) => c.setCode && c.collectorNumber);
  let matchingStrategy: MatchingStrategy;
  if (hasSourceIds) {
    matchingStrategy = 'source_id';
  } else if (hasSetAndCollector) {
    matchingStrategy = 'fingerprint';
  } else {
    matchingStrategy = 'none';
  }

  // Determine format version for DelverLens
  let formatVersion: string | undefined;
  if (effectiveSource === 'delverlens') {
    formatVersion = isDelverV2 ? 'v2' : 'v1';
  }

  // Calculate missing fields
  const expectedFieldNames = valueMaps.expectedFields;
  const fieldsMissing = expectedFieldNames
    .filter((f) => !foundCanonicalFields.has(f))
    .map((f) => {
      // Reverse-map canonical field to a representative header name
      for (const [h, c] of Object.entries(HEADER_ALIASES)) {
        if (c === f) return h;
      }
      return f;
    });

  const fieldSummary: ImportFieldSummary = {
    sourceName: valueMaps.displayName,
    fieldsUsed,
    fieldsIgnored,
    matchingStrategy,
    ...(formatVersion ? { formatVersion } : {}),
    ...(fieldsMissing.length > 0 ? { fieldsMissing } : {}),
  };

  return { cards, fieldSummary, detectedSource };
}
